import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Fade,
  Slide,
  Grow
} from '@mui/material';
import {
  Lightbulb,
  TrendingUp,
  Psychology,
  Favorite,
  Star,
  ArrowBack
} from '@mui/icons-material';

const AboutPage = ({ onBack }) => {
  const teamMembers = [
    {
      name: 'Ishayan Das',
      role: 'Co-Founder',
      avatar: 'ID'
    },
    {
      name: 'Rohan Vijendran',
      role: 'Co-Founder',
      avatar: 'RV'
    },
    {
      name: 'Shradha Rachamreddy',
      role: 'Co-Founder',
      avatar: 'SR'
    }
  ];

  const coreValues = [
    {
      title: 'Initiative',
      description: 'We take ownership of challenges and lead with action. We don\'t wait for change—we create it.',
      icon: TrendingUp
    },
    {
      title: 'Impact',
      description: 'We focus on meaningful, measurable outcomes that improve real lives. Purpose drives everything we do.',
      icon: Favorite
    },
    {
      title: 'Adaptability',
      description: 'We stay flexible, learn from failure, and continuously improve. Growth is part of the process.',
      icon: Psychology
    },
    {
      title: 'Authenticity',
      description: 'We stay true to our values and our community. We lead with heart - not polish - because real change starts with being real.',
      icon: Star
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      pt: 8
    }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Box textAlign="center" mb={8}>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'white', 
                fontWeight: 700,
                mb: 3,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              About Pathly
            </Typography>
          </Box>
        </Fade>

        {/* Mission Statement */}
        <Slide direction="up" in timeout={1000}>
          <Card sx={{ 
            mb: 8, 
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 4
          }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              {/* Exact filled Lightbulb SVG with left-to-right gradient */}
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 24 }}>
                <defs>
                  <linearGradient id="lightbulb-gradient" x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#667eea" />
                    <stop offset="1" stopColor="#764ba2" />
                  </linearGradient>
                </defs>
                <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C7.79 2 5 4.79 5 8c0 2.13 1.2 4.04 3 5.19V15c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-1.81c1.8-1.15 3-3.06 3-5.19 0-3.21-2.79-6-6-6z" fill="url(#lightbulb-gradient)"/>
              </svg>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Our Mission
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 900, mx: 'auto' }}>
                We empower high school students to discover who they are by connecting their passions to real-world opportunities, 
                and helping them find ways to further such passions. And creating purpose-driven experiences that create impact, 
                fuel personal growth, and help them stand out on their path to college and beyond.
              </Typography>
            </CardContent>
          </Card>
        </Slide>

        {/* Core Values */}
        <Box mb={8}>
          <Typography 
            variant="h3" 
            textAlign="center" 
            sx={{ 
              color: 'white', 
              fontWeight: 600, 
              mb: 6,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Our Core Values
          </Typography>
          <Grid container spacing={4}>
            {coreValues.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={1200 + index * 200}>
                  <Card sx={{ 
                    height: '100%',
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 3,
                        color: 'unset'
                      }}>
                        {/* Custom SVGs for core values with exact MUI icon paths and gradient */}
                        {index === 0 ? (
                          // TrendingUp
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <linearGradient id="trendingup-gradient" x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#667eea" />
                                <stop offset="1" stopColor="#764ba2" />
                              </linearGradient>
                            </defs>
                            <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.59-6.59L22 12V6z" fill="url(#trendingup-gradient)"/>
                          </svg>
                        ) : index === 1 ? (
                          // Favorite (Heart)
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <linearGradient id="heart-gradient" x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#667eea" />
                                <stop offset="1" stopColor="#764ba2" />
                              </linearGradient>
                            </defs>
                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="url(#heart-gradient)"/>
                          </svg>
                        ) : index === 2 ? (
                          // Exact filled Psychology (head with gear) SVG
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <linearGradient id="psychology-gradient" x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#667eea" />
                                <stop offset="1" stopColor="#764ba2" />
                              </linearGradient>
                            </defs>
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="url(#psychology-gradient)"/>
                          </svg>
                        ) : index === 3 ? (
                          // Star
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <linearGradient id="star-gradient" x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#667eea" />
                                <stop offset="1" stopColor="#764ba2" />
                              </linearGradient>
                            </defs>
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill="url(#star-gradient)"/>
                          </svg>
                        ) : null}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Team Section */}
        <Box>
          <Typography 
            variant="h3" 
            textAlign="center" 
            sx={{ 
              color: 'white', 
              fontWeight: 600, 
              mb: 6,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Meet Our Team
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            sx={{ 
              color: 'rgba(255,255,255,0.8)', 
              mb: 6,
              fontWeight: 300
            }}
          >
            Three passionate co-founders dedicated to empowering students
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={1400 + index * 200}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          mx: 'auto', 
                          mb: 3,
                          background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                          fontSize: '1.5rem',
                          fontWeight: 600
                        }}
                      >
                        {member.avatar}
                      </Avatar>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {member.name}
                      </Typography>
                      <Chip 
                        label={member.role} 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Back Button */}
        <Box textAlign="center" mt={8}>
          <Slide direction="up" in timeout={1600}>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  mb: 3,
                  fontWeight: 300
                }}
              >
                Ready to start your journey?
              </Typography>
              <Box 
                onClick={onBack}
                sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  color: 'white',
                  padding: 2,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <ArrowBack />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Back to Home
                </Typography>
              </Box>
            </Box>
          </Slide>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutPage; 